import React from 'react';
import { ExtendedBrand, BrandFormData } from './BrandTypes';
import { getQrCodeUrl, hasDocument, getImageUrl } from './BrandKycUtils';
import { QRCodeSVG } from 'qrcode.react';
import blackLogo from '../../assets/CleverPe Logos/CleverPe_Black_icon.svg';
import { Edit, Trash2, ClipboardCheck, Clock, Undo2, Ban } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { BasicDetailsTab } from './tabs/BasicDetailsTab';
import { BankKycTab } from './tabs/BankKycTab';
import { ShopKycTab } from './tabs/ShopKycTab';
import { DocumentsKycTab } from './tabs/DocumentsKycTab';
import { showToast } from '../../utils/toast';
import { updateBrand } from "../../services/brand";


interface BrandDetailTabsProps {
  selectedBrand: ExtendedBrand;
  setSelectedBrand: React.Dispatch<React.SetStateAction<ExtendedBrand | null>>;
  editData: BrandFormData;
  isEditing: boolean;
  activeDetailsTab: 'basic' | 'bank' | 'shop' | 'docs' | 'logs';
  setActiveDetailsTab: React.Dispatch<React.SetStateAction<'basic' | 'bank' | 'shop' | 'docs' | 'logs'>>;
  errors: Record<string, string>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  isUpdating: boolean;
  handleSave: () => void;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  canManageBrands: boolean;
  isBankKycEditing: boolean;
  isShopKycEditing: boolean;
  isDocsKycEditing: boolean;
  bankKycData: { accountNo: string; holderName: string; ifsc: string } | null;
  shopKycData: { name: string; address: string; state: string; city: string; pin: string } | null;
  docsKycFiles: { shopFront: File | null; shopInner: File | null; evCer: File | null };
  handleEditBankKyc: () => void;
  handleEditShopKyc: () => void;
  handleEditDocsKyc: () => void;
  handleBankKycChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleShopKycChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>, docType: 'shopFront' | 'shopInner' | 'evCer') => void;
  setIsBankKycEditing: React.Dispatch<React.SetStateAction<boolean>>;
  setIsShopKycEditing: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDocsKycEditing: React.Dispatch<React.SetStateAction<boolean>>;
  saveBankKyc: () => void;
  saveShopKyc: () => void;
  saveDocsKyc: () => void;
  handleDeleteConfirm: () => void;
  setDocsKycFiles: React.Dispatch<React.SetStateAction<{ shopFront: File | null; shopInner: File | null; evCer: File | null }>>;
}

export const BrandDetailTabs: React.FC<BrandDetailTabsProps> = ({
  selectedBrand,
  setSelectedBrand,
  editData,
  isEditing,
  activeDetailsTab,
  setActiveDetailsTab,
  errors,
  handleChange,
  isUpdating,
  handleSave,
  setIsEditing,
  canManageBrands,
  isBankKycEditing,
  isShopKycEditing,
  isDocsKycEditing,
  bankKycData,
  shopKycData,
  docsKycFiles,
  handleEditBankKyc,
  handleEditShopKyc,
  handleEditDocsKyc,
  handleBankKycChange,
  handleShopKycChange,
  handleFileChange,
  setIsBankKycEditing,
  setIsShopKycEditing,
  setIsDocsKycEditing,
  saveBankKyc,
  saveShopKyc,
  saveDocsKyc,
  handleDeleteConfirm,
  setDocsKycFiles
}) => {
  return (
    <div className="space-y-8">
      {/* Brand header with icon */}
      {!isEditing && (
        <div className="flex items-center mb-2">
          <div className="h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold mr-4">
            {selectedBrand.name.charAt(0)}
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-xl font-semibold text-gray-800">{selectedBrand.name}</h3>
              <span className="text-[12-px] text-gray-700">
                ({selectedBrand.isBrand ? "Brand" : "Partner"})
              </span>
            </div>

            <p className="text-sm text-gray-500">ID: {selectedBrand._id}</p>
          </div>
        </div>
      )}

      {/* Brand QR Code section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-7 rounded-xl mb-6">
        <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/3 mb-4 md:mb-0 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Merchant Registration</h3>
            <p className="text-sm text-gray-600 mb-4">
              Scan this QR code to register as a merchant. Share this with approved merchants only.
            </p>
            <div className="flex items-center">
              <p className="text-xs text-gray-500 break-all bg-white bg-opacity-50 p-2 rounded border border-gray-200 flex-grow mr-2">
                URL: {getQrCodeUrl(selectedBrand.token)}
              </p>
              <button
                onClick={() => copyLinkToClipboard(selectedBrand.token)}
                className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded"
                title="Copy link to clipboard"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>

          <div className="md:w-2/3 flex justify-center md:justify-end">
            <div className="bg-white p-4 rounded-lg shadow-md border border-blue-100">
              <QRCodeSVG
                value={getQrCodeUrl(selectedBrand.token)}
                size={200}
                level="H"
                imageSettings={{
                  src: blackLogo,
                  height: 45,
                  width: 45,
                  excavate: true,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          <button
            type="button"
            className={`mr-2 py-2 px-4 text-sm font-medium border-b-2 ${activeDetailsTab === 'basic'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => setActiveDetailsTab('basic')}
          >
            Basic Details
          </button>
          <button
            type="button"
            className={`mr-2 py-2 px-4 text-sm font-medium border-b-2 ${activeDetailsTab === 'bank'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => setActiveDetailsTab('bank')}
          >
            Bank Details {selectedBrand.kyc?.bank ? '✓' : ''}
          </button>
          <button
            type="button"
            className={`mr-2 py-2 px-4 text-sm font-medium border-b-2 ${activeDetailsTab === 'shop'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => setActiveDetailsTab('shop')}
          >
            Shop Details {selectedBrand.kyc?.shop ? '✓' : ''}
          </button>
          <button
            type="button"
            className={`mr-2 py-2 px-4 text-sm font-medium border-b-2 ${activeDetailsTab === 'docs'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => setActiveDetailsTab('docs')}
          >
            Documents {selectedBrand.kyc?.docs ? '✓' : ''}
          </button>
          <button
            type="button"
            className={`py-2 px-4 text-sm font-medium border-b-2 ${activeDetailsTab === 'logs'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => setActiveDetailsTab('logs')}
          >
            Logs
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeDetailsTab === 'basic' && (
        <BasicDetailsTab
          selectedBrand={selectedBrand}
          editData={editData}
          isEditing={isEditing}
          errors={errors}
          handleChange={handleChange}
        />
      )}

      {activeDetailsTab === 'bank' && (
        <BankKycTab
          selectedBrand={selectedBrand}
          isBankKycEditing={isBankKycEditing}
          bankKycData={bankKycData}
          errors={errors}
          handleBankKycChange={handleBankKycChange}
          setIsBankKycEditing={setIsBankKycEditing}
          saveBankKyc={saveBankKyc}
          isUpdating={isUpdating}
          canManageBrands={canManageBrands}
          handleEditBankKyc={handleEditBankKyc}
        />
      )}

      {activeDetailsTab === 'shop' && (
        <ShopKycTab
          selectedBrand={selectedBrand}
          isShopKycEditing={isShopKycEditing}
          shopKycData={shopKycData}
          errors={errors}
          handleShopKycChange={handleShopKycChange}
          setIsShopKycEditing={setIsShopKycEditing}
          saveShopKyc={saveShopKyc}
          isUpdating={isUpdating}
          canManageBrands={canManageBrands}
          handleEditShopKyc={handleEditShopKyc}
        />
      )}

      {activeDetailsTab === 'docs' && (
        <DocumentsKycTab
          selectedBrand={selectedBrand}
          isDocsKycEditing={isDocsKycEditing}
          docsKycFiles={docsKycFiles}
          errors={errors}
          handleFileChange={handleFileChange}
          setIsDocsKycEditing={setIsDocsKycEditing}
          saveDocsKyc={saveDocsKyc}
          isUpdating={isUpdating}
          canManageBrands={canManageBrands}
          handleEditDocsKyc={handleEditDocsKyc}
          handleDeleteConfirm={handleDeleteConfirm}
          setDocsKycFiles={setDocsKycFiles}
        />
      )}

      {activeDetailsTab === 'logs' && (
        <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-700">Logs</h3>
          </div>

          {selectedBrand.logs && selectedBrand.logs.length > 0 ? (
            <div className="divide-y divide-gray-200 max-h-[400px] overflow-y-auto">
              {selectedBrand.logs.slice().reverse().map((log, index) => (
                <div key={index} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-3 flex-shrink-0">
                      <ClipboardCheck size={16} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-700">{log}</p>
                      <div className="mt-1 flex items-center text-xs text-gray-500">
                        <Clock size={12} className="mr-1" />
                        <span>
                          {log.includes(" at ") ? log.split(" at ")[1] : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-4">
                <ClipboardCheck size={24} />
              </div>
              <p className="text-gray-500 text-sm">No logs are available for this brand</p>
            </div>
          )}
        </div>
      )}

      {/* Actions - Only show for admin/superadmin users */}
      {/* Actions - Only show for admin/superadmin users */}
      {canManageBrands && (
        <div className="flex justify-end space-x-3 pt-5">

          {/* Delete Brand */}
          {!isEditing && (
            <Button
              variant="outline"
              color="red"
              onClick={handleDeleteConfirm}
              className="flex items-center"
            >
              <Trash2 size={16} className="mr-1.5" />
              Delete
            </Button>
          )}

          {/* Edit Brand */}
          {!isEditing && (
            <Button
              onClick={() => setIsEditing(true)}
              className="flex items-center"
            >
              <Edit size={16} className="mr-1.5" />
              Edit
            </Button>
          )}

          {/* Suspend / Restore Brand */}
          {/* Suspend / Restore Brand */}
          {!isEditing && (
            <>
              {selectedBrand.suspended ? (
                /* RESTORE BUTTON */
                <Button
                  size="sm"
                  onClick={async () => {
                    const willSuspend = false;  // restoring
                    try {
                      const token = localStorage.getItem("token");

                      const updatedBrand = await updateBrand(
                        selectedBrand._id,
                        { suspended: willSuspend },
                        token
                      );

                      if (updatedBrand) {
                        setSelectedBrand(prev => ({
                          ...prev!,
                          ...updatedBrand,
                          groupId: updatedBrand.groupId ?? "",
                        }));

                        showToast.success("Brand restored");
                      } else {
                        showToast.error("Brand update failed: No data returned");
                      }
                    } catch (err) {
                      console.error("[RESTORE ERROR]:", err);
                      showToast.error("Failed to restore brand");
                    }
                  }}
                  className="bg-blue-600 text-white hover:bg-blue-700 flex items-center"
                >
                  <Undo2 size={16} className="mr-1" />
                  Restore
                </Button>
              ) : (
                /* SUSPEND BUTTON */
                <Button
                  size="sm"
                  onClick={async () => {
                    const willSuspend = true; // suspending
                    try {
                      const token = localStorage.getItem("token");

                      const updatedBrand = await updateBrand(
                        selectedBrand._id,
                        { suspended: willSuspend },
                        token
                      );

                      if (updatedBrand) {
                        setSelectedBrand(prev => ({
                          ...prev!,
                          ...updatedBrand,
                          groupId: updatedBrand.groupId ?? "",
                        }));

                        showToast.success("Brand suspended");
                      } else {
                        showToast.error("Brand update failed: No data returned");
                      }
                    } catch (err) {
                      console.error("[SUSPEND ERROR]:", err);
                      showToast.error("Failed to suspend brand");
                    }
                  }}
                  className="bg-red-600 text-white hover:bg-red-700 flex items-center"
                >
                  <Ban size={16} className="mr-1" />
                  Suspend
                </Button>
              )}
            </>
          )}



          {/* Editing Mode Buttons */}
          {isEditing && (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button isLoading={isUpdating} onClick={handleSave}>
                {isUpdating ? "Saving..." : "Save Changes"}
              </Button>
            </>
          )}
        </div>
      )}


    </div>
  );
};

// Copy link to clipboard
const copyLinkToClipboard = (token?: string) => {
  const url = getQrCodeUrl(token);
  navigator.clipboard.writeText(url)
    .then(() => {
      showToast.success('Merchant registration link copied to clipboard');
    })
    .catch((error) => {
      console.error('Failed to copy link:', error);
      showToast.error('Failed to copy link to clipboard');
    });
};